import React, { useState } from 'react';
import intl from 'react-intl-universal';
import { ChildDataProps, Query, QueryResult } from 'react-apollo';
import _get from 'lodash/get';
import { RouteComponentProps } from 'react-router-dom';
import { SortingRule } from 'react-table';
import { DWHPastConferenceType, DWHPastMeetingsQueryResponseType } from 'interfaces/Dashboard';
import { exportToCsv } from 'utils/csv-utils';
import { parsePastMeetingsDataForExport } from 'utils/exportUtils';
import { SortDirection } from 'constants/queryConstants';
import InfiniteTable from 'containers/InfiniteTable/InfiniteTableContainer';
import ListViewLayout from 'components/ListViewLayout/ListViewLayout';
import ReportHeader from '../ReportHeader';
import { SetSort } from 'interfaces/Tables';
import pastMeetingsColumnConfig from './PastMeetingsColumnConfig';
import PastMeetingsTabQuery from 'queries/pastMeetings/PastMeetingsTabQuery.gql';
import { PaginatedCallHistory } from 'typings/types';
import Metadata = PaginatedCallHistory.Metadata;
import { withRouter } from 'react-router';
import logger from 'utils/logger';
import { useSelector } from 'react-redux';
import { accountSelectors } from '@lifesize/nucleus';
import {
  ACCOUNT_FEATURE_PAST_MEETINGS_ENABLED
} from 'constants/accountFeatureConstants';
import { selectEcommerceLoading, selectEcommerceSupported } from 'selectors/ecommerceSelector';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import EcommercePrompt from 'components/EcommercePrompt/EcommercePrompt';
import { PLAN_TYPE_HOST_PLUS } from 'constants/ecommerceConstants';

interface Props extends RouteComponentProps {
  isFetching: boolean;
  startTimestamp: number;
  endTimestamp: number;
  searchString: string;
  pageNumber: number;
  fetchMoreRows: Function;
  listData: Array<DWHPastConferenceType>;
  setSort: (sort: SetSort) => void;
  sort: Metadata;
  search: string;
}

type CallHistoryResponse = {
  pastMeetings: DWHPastMeetingsQueryResponseType;
};

let cursor = false;

const PastMeetingsTab = (props: ChildDataProps<Props, CallHistoryResponse>) => {
  const {
    setSort,
    history,
  } = props;
  const [isExporting, setIsExporting] = useState(false);

  const accountFeaturePastMeetings = useSelector(accountSelectors.getAccountFeature)(ACCOUNT_FEATURE_PAST_MEETINGS_ENABLED);
  const isEcommerceSupported = useSelector(selectEcommerceSupported);
  const ecommerceAccountLoading = useSelector(selectEcommerceLoading);

  if (ecommerceAccountLoading) {
    return <LoadingIndicator />;
  }

  if (!accountFeaturePastMeetings && isEcommerceSupported) {
    return (
      <EcommercePrompt
        message={intl.get('pastMeetingsUpgradeMessage')}
        extraParams={{
          plan: PLAN_TYPE_HOST_PLUS
        }}
      />
    );
  }

  const onFetchData = ({sorted: [column]}: { sorted: Array<SortingRule>}) => {
    if (!column) {
      return;
    }
    setSort({
      sortBy: column.id,
      order: column.desc ? SortDirection.DESC : SortDirection.ASC,
    });
  };

  const { sortBy, order } = props.sort;
  const { startTimestamp, endTimestamp, search } = props;
  const queryVariables = { sortBy, order, startTimestamp, endTimestamp, search };

  const handleExportToCsv = async (fetchMoreRows: (callback: (freshData: []) => void) => Promise<{}>, listData: []) => {
    let compiledData = [...listData];
    setIsExporting(true);
    let isFetching = false;
    const updateData = async () => {
      isFetching = true;
      try {
        await fetchMoreRows((freshData) => {
          compiledData = freshData;
          isFetching = false;
        });
      } catch (e) {
        logger.error('Cannot fetch more past meetings');
        cursor = false;
        isFetching = false;
      }
    };

    const collectForExportInterval = setInterval(
      async () => {
        if (!isFetching) {
          if (!cursor) {
            clearInterval(collectForExportInterval);
            const exportData = compiledData
              .map(parsePastMeetingsDataForExport);
            exportToCsv(intl.get('pastMeetings'), exportData);
            setIsExporting(false);
          } else {
            await updateData();
          }
        }
      },
      200
    );
  };

  return (
    <Query query={PastMeetingsTabQuery} variables={{ ...queryVariables }} fetchPolicy="network-only">
      {({ loading, error, data, fetchMore }: QueryResult<object>) => {
        const listData = !loading ? _get(data, 'pastMeetings.conferences', []) : [];
        cursor = _get(data, 'pastMeetings.metadata.cursor', false);

        const fetchMoreRows = async (callback?: Function) => {
          // If directory list isn't set our initial load hasn't finished yet, so we
          // can't fetch more. We also bail if there are no more records to load.
          if (!cursor) {
            return {};
          }

          return fetchMore({
            variables: {
              cursor
            }, // Pass in the previous cursor to our query.
            // tslint:disable-next-line
            updateQuery: (prev: any, {fetchMoreResult}: any) => {
              // Merge our new results into previous cached results.
              if (_get(fetchMoreResult, 'errors', false)) {
                throw _get(fetchMoreResult, 'errors', false);
              }
              const previousDirectoryList = _get(prev, 'pastMeetings', {});
              const fetchMoreResultDirectoryList = _get(fetchMoreResult, 'pastMeetings', {});
              const conferences = [
                ..._get(previousDirectoryList, 'conferences', []),
                ..._get(fetchMoreResultDirectoryList, 'conferences', []),
              ];
              if (callback) {
                callback(conferences);
              }
              return {
                ...prev,
                pastMeetings: {
                  ...previousDirectoryList,
                  metadata: _get(fetchMoreResultDirectoryList, 'metadata', {}),
                  conferences,
                },
              };
            },
          });
        };

        return (
          <ListViewLayout
            hideHeader={true}
            isFetching={false}
            numberOfPages={0}
            error={_get(error, 'message', undefined)}
          >
            <InfiniteTable
              columnConfig={pastMeetingsColumnConfig(history)}
              data={listData}
              defaultSort={[
                {
                  id: 'startedAt',
                  desc: true
                }
              ]}
              fetchMore={() => fetchMoreRows()}
              getTdProps={() => ({
                style: {
                  alignItems: 'flex-start',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  lineHeight: '12px'
                }
              })}
              getTheadTrProps={() => ({
                style: {
                  paddingLeft: '5px'
                }
              })}
              getTrProps={() => ({
                style: {
                  paddingLeft: '5px'
                }
              })}
              hasMoreRecords={cursor}
              keyField={'startedAt'}
              loading={loading}
              onFetchData={onFetchData}
              renderHeader={() => (
                <ReportHeader
                  handleExportData={() => {
                    handleExportToCsv(fetchMoreRows, listData);
                  }}
                  isExporting={isExporting}
                />
              )}
              rowHeightOverride={44}
            />
          </ListViewLayout>
        );
      }}
    </Query>
  );
};

export default withRouter(PastMeetingsTab);
